// Pure mapping functions from car state to synthesis parameters. Kept free of
// Web Audio so they can be unit-tested in node; the node graph in
// carAudio.ts only applies what these return.

export const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);

export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Engine speed range of the cosmetic gearbox (gearbox.ts). */
export const IDLE_RPM = 4000;
export const MAX_RPM = 12500;

/** 0..1 position of rpm within the gearbox's range. */
export function rpmNorm(rpm: number): number {
  return clamp((rpm - IDLE_RPM) / (MAX_RPM - IDLE_RPM), 0, 1);
}

/**
 * Firing frequency of a four-stroke V6: every cylinder fires once per two
 * crank revolutions, so 3 firings per revolution. 12,000 rpm → 600 Hz.
 */
export function firingHz(rpm: number): number {
  return (Math.max(0, rpm) / 60) * 3;
}

/**
 * Engine tone colour. On throttle the exhaust is open and bright; off throttle
 * (overrun) it is muffled. Returns the low-pass cutoff in Hz.
 */
export function engineCutoffHz(rpm: number, load: number): number {
  const r = rpmNorm(rpm);
  const off = 700 + 900 * r;
  const on = 2400 + 4600 * r;
  return off + (on - off) * clamp(load, 0, 1);
}

/** Engine loudness 0..1 (before the layer level): louder on throttle and with rpm. */
export function engineGain(rpm: number, load: number): number {
  const r = rpmNorm(rpm);
  const l = clamp(load, 0, 1);
  return (0.32 + 0.18 * r) + (0.38 + 0.12 * r) * l;
}

/** Drive into the waveshaper: more grit on throttle and at high rpm. */
export function engineDrive(rpm: number, load: number): number {
  return 1.2 + 1.6 * clamp(load, 0, 1) + 0.8 * rpmNorm(rpm);
}

/**
 * How much the engine is on the overrun (high rpm, throttle closed), 0..1.
 * Drives the crackle/pop rate.
 */
export function overrunAmount(rpm: number, throttle: number): number {
  return smoothstep(6500, 10500, rpm) * (1 - smoothstep(0.05, 0.25, throttle));
}

/**
 * Turbo spool target 0..1 from rpm × throttle. The actual spool follows with
 * lag (see approach()).
 */
export function turboTarget(rpm: number, throttle: number): number {
  return smoothstep(5000, 11500, rpm) * Math.pow(clamp(throttle, 0, 1), 1.3);
}

/** Turbo whistle pitch from spool (0..1), Hz. */
export function turboHz(spool: number): number {
  return 1900 + 3600 * clamp(spool, 0, 1);
}

/**
 * First-order lag toward a target with different time constants up and down
 * (s). Frame-rate independent.
 */
export function approach(current: number, target: number, dt: number, tauUp: number, tauDown: number): number {
  const tau = target > current ? tauUp : tauDown;
  if (tau <= 0) return target;
  const k = 1 - Math.exp(-Math.max(0, dt) / tau);
  return current + (target - current) * k;
}

/**
 * MGU-K whine pitch, Hz. The motor is treated as geared to the wheels, so the
 * pitch follows road speed (m/s), not engine rpm. `hzPerMs` is a tuning knob.
 */
export function ersWhineHz(speed: number, hzPerMs: number): number {
  return 90 + hzPerMs * Math.max(0, speed);
}

/** MGU-K whine gains 0..1 for deploy (+kW) and harvest (−kW) power. */
export function ersGains(mguKW: number, speed: number): { deploy: number; harvest: number } {
  const fade = smoothstep(1, 8, speed);
  const p = clamp(Math.abs(mguKW) / 350, 0, 1);
  const g = Math.pow(p, 0.7) * fade;
  return mguKW >= 0 ? { deploy: g, harvest: 0 } : { deploy: 0, harvest: g };
}

/** Tunables for the tyre-squeal curve (see squealAmount). */
export interface SquealCurve {
  /** Grip usage where squeal starts (0..1). */
  onset: number;
  /** Slip angle (rad) where slip alone starts to add squeal (sliding past peak). */
  slipOnset: number;
  /** Slip angle (rad) for full slide squeal. */
  slipFull: number;
}

export const DEFAULT_SQUEAL: SquealCurve = { onset: 0.92, slipOnset: 0.1, slipFull: 0.2 };

/**
 * Squeal amount 0..1 for one axle. Silent in normal driving, rising steeply
 * over the last few percent of grip so the limit is clearly audible. Past the
 * peak slip angle the tyre force falls (grip use drops below 1) while the tyre
 * slides, so slip angle adds squeal on its own. Faded out near standstill,
 * where the low-speed slip angles are meaningless.
 */
export function squealAmount(gripUse: number, slip: number, speed: number, c: SquealCurve = DEFAULT_SQUEAL): number {
  const g = smoothstep(c.onset, 1.0, gripUse);
  const s = smoothstep(c.slipOnset, c.slipFull, Math.abs(slip));
  const a = Math.max(g * g * (1.4 - 0.4 * g), s);
  return clamp(a, 0, 1) * smoothstep(3, 10, speed);
}

/** Squeal band centre, Hz: rises slightly with speed and slide amount. */
export function squealHz(base: number, speed: number, amount: number): number {
  return base * (1 + 0.12 * clamp(speed / 80, 0, 1.3) + 0.08 * amount);
}

export type AudioSurface = 'track' | 'kerb' | 'runoff-asphalt' | 'grass' | 'gravel' | 'outside';

/** How much a surface lets tyres squeal (grass and gravel don't). */
export function surfaceSquealFactor(surface: AudioSurface): number {
  return surface === 'grass' || surface === 'gravel' ? 0.1 : surface === 'outside' ? 0 : 1;
}

/**
 * Kerb ridge pulse rate, Hz: the car crosses ridges at speed / spacing. At
 * 200 km/h with 0.7 m spacing that is ~79 Hz, the familiar onboard "brrr".
 */
export function kerbPulseHz(speed: number, spacing = 0.7): number {
  return clamp(Math.max(0, speed) / spacing, 6, 110);
}

/** Surface rumble voicing for the off-track / kerb layer. */
export interface SurfaceVoice {
  /** Overall level 0..1 (before the layer level). */
  gain: number;
  /** Band-pass centre, Hz. */
  bandHz: number;
  /** Band-pass Q. */
  q: number;
  /** Periodic AM rate (Hz) and depth 0..1 (ridges). */
  pulseHz: number;
  pulseDepth: number;
  /** Random AM depth 0..1 (stones, clumps) and its rate (random points per second). */
  randomDepth: number;
  randomHz: number;
  /** Low thump tone level 0..1 (felt more than heard; for headphones). */
  thump: number;
}

/** Reusable output object to avoid per-frame allocation. */
export function surfaceVoice(surface: AudioSurface, speed: number, out: SurfaceVoice): SurfaceVoice {
  const v = Math.max(0, speed);
  const moving = smoothstep(2, 12, v);
  switch (surface) {
    case 'kerb':
      out.gain = moving * (0.55 + 0.45 * clamp(v / 70, 0, 1));
      out.bandHz = 260 + 2.5 * v;
      out.q = 1.1;
      out.pulseHz = kerbPulseHz(v);
      out.pulseDepth = 0.95;
      out.randomDepth = 0.1;
      out.randomHz = 60;
      out.thump = 0.8;
      break;
    case 'grass':
      out.gain = moving * (0.35 + 0.3 * clamp(v / 60, 0, 1));
      out.bandHz = 320;
      out.q = 0.7;
      out.pulseHz = clamp(v / 3, 2, 30);
      out.pulseDepth = 0.25;
      out.randomDepth = 0.6;
      out.randomHz = 12 + v * 0.6;
      out.thump = 0.5;
      break;
    case 'gravel':
      out.gain = moving * (0.45 + 0.3 * clamp(v / 50, 0, 1));
      out.bandHz = 1500 + 6 * v;
      out.q = 0.8;
      out.pulseHz = clamp(v / 1.5, 3, 60);
      out.pulseDepth = 0.15;
      out.randomDepth = 0.9;
      out.randomHz = 90 + v * 4;
      out.thump = 0.3;
      break;
    default:
      out.gain = 0;
      out.bandHz = 400;
      out.q = 0.8;
      out.pulseHz = 10;
      out.pulseDepth = 0;
      out.randomDepth = 0;
      out.randomHz = 20;
      out.thump = 0;
  }
  return out;
}

export function newSurfaceVoice(): SurfaceVoice {
  return { gain: 0, bandHz: 400, q: 0.8, pulseHz: 10, pulseDepth: 0, randomDepth: 0, randomHz: 20, thump: 0 };
}

/** Wind/road noise level 0..1, rising with speed². */
export function windGain(speed: number): number {
  const r = clamp(speed / 90, 0, 1.2);
  return r * r;
}

/** Wind low-pass cutoff, Hz. */
export function windCutoffHz(speed: number): number {
  return 350 + 18 * clamp(speed, 0, 110);
}

/** Impact one-shot intensity 0..1 from speed lost (m/s); 0 below the threshold. */
export function impactIntensity(lost: number): number {
  if (!(lost > 0.8)) return 0;
  return clamp(Math.pow((lost - 0.8) / 14, 0.6), 0.08, 1);
}

/** Probability-per-second of an overrun pop. */
export function popRate(overrun: number, sinceLift: number): number {
  // Strongest just after lifting, then a sparse tail of crackle.
  return overrun * (4 + 22 * Math.exp(-sinceLift / 0.6));
}

/** Small deterministic PRNG (mulberry32) so offline renders are reproducible. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Harmonic amplitudes for the engine's periodic wave, whose fundamental is
 * half the firing frequency (crank order 1.5). Even harmonics are the firing
 * orders (strong); odd ones are the half orders from bank-to-bank pulse
 * differences, which give a V6 its rasp. Index 0 is DC (0).
 */
export function engineHarmonics(count: number): Float32Array<ArrayBuffer> {
  const amp = new Float32Array(count + 1);
  const rng = makeRng(6);
  for (let n = 1; n <= count; n++) {
    const firingOrder = n % 2 === 0;
    const k = n / 2; // multiple of the firing frequency
    let a = firingOrder ? 1 / Math.pow(k, 0.75) : 0.42 / Math.pow(k + 0.5, 0.9);
    // A broad exhaust resonance around the 3rd–5th firing harmonics.
    a *= 1 + 0.6 * Math.exp(-((k - 4) * (k - 4)) / 4);
    // Fixed pseudo-random variation so it doesn't sound like a textbook saw.
    a *= 0.75 + 0.5 * rng();
    amp[n] = a;
  }
  amp[0] = 0;
  return amp;
}

/** Soft-saturation curve for a WaveShaper. Asymmetric for even-order grit. */
export function shaperCurve(samples: number, asymmetry: number): Float32Array<ArrayBuffer> {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    c[i] = Math.tanh(2.2 * x + asymmetry * x * x);
  }
  // Remove the DC offset the asymmetric term introduces at x = 0.
  const mid = c[(samples - 1) >> 1]!;
  for (let i = 0; i < samples; i++) c[i] = c[i]! - mid;
  return c;
}

/**
 * Output safety clipper: identity below `knee`, smooth approach to `ceiling`.
 * Input is pre-scaled by 1/`range` so inputs up to ±range map softly.
 */
export function safetyCurve(samples: number, knee = 0.8, ceiling = 0.98, range = 2): Float32Array<ArrayBuffer> {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = ((i / (samples - 1)) * 2 - 1) * range;
    c[i] = softLimit(x, knee, ceiling);
  }
  return c;
}

export function softLimit(x: number, knee: number, ceiling: number): number {
  const a = Math.abs(x);
  if (a <= knee) return x;
  const room = ceiling - knee;
  return Math.sign(x) * (knee + room * Math.tanh((a - knee) / room));
}

/** Uniform white noise, deterministic. */
export function whiteNoise(length: number, seed = 1): Float32Array<ArrayBuffer> {
  const out = new Float32Array(length);
  const rng = makeRng(seed);
  for (let i = 0; i < length; i++) out[i] = rng() * 2 - 1;
  return out;
}
